import { useState, useEffect, useMemo, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { API_BASE } from "@/lib/api";
import { authFetch } from "@/lib/auth-client";
import { ribbonStanding } from "@/components/events/ribbon-standing";
import { getTopPrize, TopPrizeBadge } from "@/components/events/PrizeDisplay";
import { FormatBadge, LengthBadge } from "@/components/events/EventBadges";
import { SectionHeader } from "@/components/ui/section-header";
import type { MainEventSummary, MainEventViewerState } from "@/services/events/main-event";
import { asset } from "@/lib/asset";
import { getSportConfig } from "@/config/sports";

const SPORT_ICONS: Record<string, string> = {
  NBA: asset("assets/sports/basketball.webp"),
  NCAA_BASKETBALL: asset("assets/sports/basketball.webp"),
  NHL: asset("assets/sports/hockey.webp"),
  NFL: asset("assets/sports/football.webp"),
  NCAA_FOOTBALL: asset("assets/sports/football.webp"),
  MLB: asset("assets/sports/baseball.webp"),
  SOCCER: asset("assets/sports/soccer.webp"),
  SOCCER_EPL: asset("assets/sports/soccer.webp"),
  SOCCER_WORLD_CUP: asset("assets/sports/soccer.webp"),
  SOCCER_UCL: asset("assets/sports/soccer.webp"),
  MMA: asset("assets/sports/mma.webp"),
  F1: asset("assets/sports/f1.webp"),
  GOLF: asset("assets/sports/golf.webp"),
  TENNIS: asset("assets/sports/tennis.webp"),
  HORSE_RACING: asset("assets/sports/horse-racing.webp"),
  ESPORTS: asset("assets/sports/esports.webp"),
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  UPCOMING: { bg: "bg-blue-500/10 border-blue-500/30", text: "text-blue-400" },
  OPEN: { bg: "bg-verdigris/10 border-verdigris/30", text: "text-verdigris" },
  LOCKED: { bg: "bg-gold/10 border-gold/30", text: "text-gold" },
  COMPLETED: { bg: "bg-tertiary/10 border-tertiary/30", text: "text-tertiary" },
};

/**
 * Compact event tile — width is capped at 2× its height (320×160), so the
 * events page reads as a grid of tiles instead of full-width rows. The top
 * prize renders as a small corner glyph (coins icon or item art).
 */
function MainEventCard({
  event,
  onClick,
}: {
  event: MainEventSummary;
  onClick: () => void;
}) {
  const sportIcon =
    SPORT_ICONS[event.sport] || asset("assets/sports/basketball.webp");
  const statusStyle = STATUS_COLORS[event.status] || STATUS_COLORS.UPCOMING;
  const startDate = new Date(event.startTime).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const endDate = new Date(event.endTime).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const topPrize = getTopPrize(event);

  return (
    <button
      onClick={onClick}
      className="on-parchment group relative flex min-h-40 w-full max-w-[320px] flex-col overflow-hidden rounded-2xl border border-parchment-border bg-parchment p-3.5 text-left shadow-sm transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-md"
    >
      <StandingRibbon viewer={event.viewer} />
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
        <Image
          src={sportIcon}
          alt={event.sport}
          width={22}
          height={22}
          className="h-[22px] w-[22px] shrink-0 object-contain opacity-85"
        />
        <span
          className={cn(
            "shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider",
            statusStyle.bg,
            statusStyle.text,
          )}
        >
          {event.status}
        </span>
        <FormatBadge format={event.format} />
        <LengthBadge startTime={event.startTime} endTime={event.endTime} durationClass={event.durationClass} />
        {event.entryFee === 0 && (
          <span className="shrink-0 rounded border border-verdigris/30 bg-verdigris/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-verdigris">
            Free
          </span>
        )}
        {event.isFull && (
          <span className="shrink-0 rounded border border-oxblood/40 bg-oxblood/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-oxblood">
            Full
          </span>
        )}
      </div>

      <h3 className="mb-0.5 line-clamp-2 font-serif text-[15px] font-bold leading-tight text-surface">
        {event.title}
      </h3>
      {/* entryCount climbs while the card is on screen; without tabular figures
          the "· N rounds" tail shuffles sideways every time it ticks. */}
      <p className="text-[11px] text-warm-gray tabular-nums">
        {startDate} — {endDate} · {event.entryCount}{event.maxEntries != null ? `/${event.maxEntries}` : ''} entered · {event.undercardCount} rounds
      </p>

      <div className="mt-auto flex items-end justify-between gap-2">
        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-tertiary transition-colors group-hover:text-gold">
          View event <ChevronRight className="h-3 w-3" />
        </span>
        {topPrize && (
          <TopPrizeBadge top={topPrize} coinIconSrc={asset("assets/icons/coins.webp")} />
        )}
      </div>
    </button>
  );
}

/**
 * Corner ribbon marking the caller's own standing in an event.
 *
 * For SURVIVOR the useful fact is not that you entered but whether you are
 * still in it, so the pool reports Alive / Eliminated and everything else
 * reports Entered. Rendered only when the API returned viewer state, so a
 * signed-out visitor (or a cached page) never sees a stale badge.
 *
 * Not aria-hidden: the label is real information, and a rotated span is read
 * normally. It sits first in the card so it is announced before the title.
 */
function StandingRibbon({ viewer }: { viewer?: MainEventViewerState }) {
  const standing = ribbonStanding(viewer);
  if (!standing) return null;
  const { label, eliminated } = standing;
  return (
    <span
      className={cn(
        // 45deg across the corner; the card's overflow-hidden clips the tails.
        "pointer-events-none absolute -right-9 top-3.5 z-10 w-32 rotate-45 py-[3px] text-center",
        "text-[8px] font-bold uppercase tracking-[0.12em] text-parchment shadow-sm",
        eliminated ? "bg-oxblood" : "bg-verdigris",
      )}
    >
      {label}
    </span>
  );
}

function sportLabel(sport: string): string {
  try {
    return getSportConfig(sport as never)?.label ?? sport.replace(/_/g, " ");
  } catch {
    return sport.replace(/_/g, " ");
  }
}

/** Horizontal, snap-scrolling rail of event tiles. */
function EventCarousel({
  events,
  onOpen,
}: {
  events: MainEventSummary[];
  onOpen: (id: string) => void;
}) {
  return (
    <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide -mx-4 px-4 sm:-mx-6 sm:px-6">
      {events.map((event) => (
        <div key={event.id} className="snap-start shrink-0 w-[300px]">
          <MainEventCard event={event} onClick={() => onOpen(event.id)} />
        </div>
      ))}
    </div>
  );
}

function EventsPageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const sportFilter = searchParams.get("sport");
  const [events, setEvents] = useState<MainEventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    authFetch(`${API_BASE}/api/main-events`)
      .then((res) => (res.ok ? res.json() : Promise.reject("Failed to fetch")))
      .then((data) => setEvents(data))
      .catch((err) =>
        setError(typeof err === "string" ? err : "Failed to load events"),
      )
      .finally(() => setLoading(false));
  }, []);

  // Admin-curated rail, soonest start first. The API returns only
  // active/upcoming events, so sport sections with nothing live disappear
  // on their own.
  const highlighted = useMemo(
    () =>
      events
        .filter((e) => e.highlighted)
        .sort(
          (a, b) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
        ),
    [events],
  );

  const sportSections = useMemo(() => {
    const bySport = new Map<string, MainEventSummary[]>();
    for (const e of events) {
      const arr = bySport.get(e.sport) ?? [];
      arr.push(e);
      bySport.set(e.sport, arr);
    }
    return [...bySport.entries()]
      .map(([sport, list]) => ({
        sport,
        events: list.sort(
          (a, b) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
        ),
      }))
      .sort(
        (a, b) =>
          new Date(a.events[0].startTime).getTime() -
          new Date(b.events[0].startTime).getTime(),
      );
  }, [events]);

  const openEvent = (id: string) => router.push(`/events/main/${id}`);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-warm-gray">Loading events...</p>
        </div>
      </div>
    );
  }

  if (sportFilter) {
    const filtered = events.filter((e) => e.sport === sportFilter);
    return (
      <div className="min-h-screen bg-surface">
        <div className="ornamental-rule max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8" />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <button
            onClick={() => router.push("/events")}
            className="mb-4 flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-tertiary transition-colors hover:text-gold"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> All events
          </button>
          <div className="mb-6 flex items-center gap-3">
            {SPORT_ICONS[sportFilter] && (
              <Image src={SPORT_ICONS[sportFilter]} alt="" width={32} height={32} className="h-8 w-8 object-contain opacity-85" />
            )}
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-cream">
              {sportLabel(sportFilter)}
            </h1>
            <span className="text-sm text-warm-gray">{filtered.length} events</span>
          </div>
          {filtered.length === 0 ? (
            <p className="text-tertiary py-12 text-center">
              No active events for this sport right now.
            </p>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,320px))] justify-start gap-4">
              {filtered.map((event) => (
                <MainEventCard key={event.id} event={event} onClick={() => openEvent(event.id)} />
              ))}
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <div className="ornamental-rule max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <span className="text-gold text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase mb-3 sm:mb-4 block">
            Events
          </span>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-cream mb-2">
            Compete &amp; Win
          </h1>
          <p className="text-sm sm:text-base text-warm-gray">
            Enter main events and pick winners across every round.
          </p>
        </div>

        {error && (
          <div className="p-4 mb-6 bg-oxblood/10 border border-oxblood/30 rounded-xl text-oxblood-text text-sm">
            {error}
          </div>
        )}

        {events.length === 0 && !error && (
          <p className="text-tertiary py-12 text-center">
            No events available right now.
          </p>
        )}

        {highlighted.length > 0 && (
          <section className="mb-10">
            <SectionHeader title="Highlighted" icon={asset("assets/icons/live-event.webp")} />
            <EventCarousel events={highlighted} onOpen={openEvent} />
          </section>
        )}

        {sportSections.map(({ sport, events: sportEvents }) => (
          <section key={sport} className="mb-10">
            <SectionHeader
              title={sportLabel(sport)}
              icon={SPORT_ICONS[sport]}
              action={{
                label: "See more",
                onClick: () => router.push(`/events?sport=${sport}`),
              }}
            />
            <EventCarousel events={sportEvents} onOpen={openEvent} />
          </section>
        ))}
      </main>
    </div>
  );
}

export default function EventsPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      }
    >
      <EventsPageInner />
    </Suspense>
  );
}
